/* global affirm */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useApolloClient } from '@apollo/client';
import {
  SET_CHECKOUT_DATA,
  AFFIRM_INSTORE_INITIATE,
  SET_INSTORE_CHECKOUT_DATA,
} from 'mocks/mutations';
import getStoreDetails from 'commons/getStoreDetails';
import { GET_CHECKOUT_DATA } from 'components/consumer/checkout/queries';
import { logInfo } from 'commons/logger';
import CheckoutSubmitButton from '../../../CheckoutSubmitButton';

import ShippingInfoBlock from '../ShippingInfoBlock';

const FinancingOption = ({
  history,
  checkout,
  showError,
  getCartIssues,
  calculateTotal,
  placeOrderLoading,
}) => {
  const [loading, setLoading] = useState(false);
  const [affirmInStore, setAffirmInStore] = useState(false);
  const [instoreOrderProcessed, setInstoreOrderProcessed] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const client = useApolloClient();

  const invokeAffirm = async () => {
    try {
      setLoading(true);
      const { cart, issues } = await getCartIssues();

      if (issues.length) {
        setLoading(false);
        return;
      }

      const {
        email_address,
        shipping,
        selected_tab,
        tab_progress,
        deliveryInfo,
        selectedStoreEmployee,
      } = checkout;

      // todo: error handling
      await client.mutate({
        mutation: SET_CHECKOUT_DATA,
        variables: {
          checkout: {
            email_address,
            shipping,
            selected_tab,
            tab_progress,
            deliveryInfo,
            selectedStoreEmployee,
          },
        },
      });

      const orderSummary = await calculateTotal();

      const orderItems = cart.map(item => ({
        display_name: item.name,
        sku: item.sku,
        unit_price: Math.round((item.price / item.quantity) * 100),
        qty: item.quantity,
        item_image_url: item.imageURL,
      }));

      const shipping_amount = Math.round(orderSummary.total_shipping * 100);
      const tax_amount = Math.round(orderSummary.tax_amount * 100);
      const total = Math.round(orderSummary.grand_total * 100);

      const checkoutInfo = {
        merchant: {
          user_confirmation_url: window.location.href,
          user_cancel_url: window.location.href,
          user_confirmation_url_action: 'GET',
          name: 'Joybird',
        },
        shipping: {
          name: {
            first: shipping.first_name,
            last: shipping.last_name,
          },
          address: {
            line1: shipping.address,
            city: shipping.city,
            state: shipping.state,
            zipcode: shipping.zipcode,
            country: 'US',
          },
          phone_number: shipping.phone_number,
          email: email_address,
        },
        billing: {
          name: {
            first: shipping.first_name,
            last: shipping.last_name,
          },
          address: {
            line1: shipping.address,
            city: shipping.city,
            state: shipping.state,
            zipcode: shipping.zipcode,
            country: 'US',
          },
          phone_number: shipping.phone_number,
          email: email_address,
        },
        items: orderItems,

        discounts: {
          discount_name: {
            discount_amount: Math.round((orderSummary.discount || 0) * 100),
          },
        },

        metadata: {
          platform_type: '4.0',
          shipping_type: 'Shipping',
          mode: 'modal',
        },

        shipping_amount,
        tax_amount,
        total,
      };

      affirm.checkout(checkoutInfo);
      affirm.checkout.open();
    } catch (e) {
      showError(
        { errors: [{ message: e.message }] },
        'Failed to invoke affirm. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const invokeInstoreAffirm = async () => {
    const orderSummary = await calculateTotal();
    const total = Math.round(orderSummary.grand_total * 100);

    const {
      email_address,
      shipping,
      selected_tab,
      tab_progress,
      deliveryInfo,
      selectedStoreEmployee,
    } = checkout;

    const setCheckoutMutation = await client.mutate({
      mutation: SET_INSTORE_CHECKOUT_DATA,
      variables: {
        checkout: {
          email_address,
          shipping,
          selected_tab,
          tab_progress,
          deliveryInfo,
          selectedStoreEmployee,
        },
        affirmInstore: true,
      },
    });

    const storeDetails = getStoreDetails();
    let userConfirmURL = window.location.href;
    const checkoutRef = setCheckoutMutation?.data?.setCheckoutData?.checkoutRef;
    if (checkoutRef) {
      const urlByParam = window.location.href.split('?');
      userConfirmURL += `${
        urlByParam.length > 1 ? `&` : `?`
      }checkout_ref=${checkoutRef}`;

      logInfo('confirm at', userConfirmURL);
    }

    const affirmCheckoutObject = {
      merchant: {
        user_cancel_url: window.location.href,
        user_confirmation_url: userConfirmURL,
        user_confirmation_url_action: 'GET',
        name: 'Joybird In-store Affirm',
      },

      billing: {
        name: {
          first: shipping.first_name,
          last: shipping.last_name,
        },
        address: {
          line1: shipping.address,
          city: shipping.city,
          state: shipping.state,
          zipcode: shipping.zipcode,
          country: 'US',
        },
        phone_number: shipping.phone_number,
        email: email_address,
      },
      shipping: {
        name: {
          first: shipping.first_name,
          last: shipping.last_name,
        },
        address: {
          line1: shipping.address,
          city: shipping.city,
          state: shipping.state,
          zipcode: shipping.zipcode,
          country: 'US',
        },
      },
      metadata: {
        entity_name: storeDetails.name || 'Joybird Store',
      },
      total,
    };

    const mutationResult = await client.mutate({
      mutation: AFFIRM_INSTORE_INITIATE,
      variables: {
        input: {
          affirmCheckoutObject,
        },
      },
    });

    if (mutationResult.data.affirmInstoreCheckoutInitiate.success) {
      setAffirmInStore(true);
    } else {
      const errorMessageText =
        mutationResult?.data?.affirmInstoreCheckoutInitiate?.errors?.[0]
          .message || 'Invalid input';

      setError(true);
      setErrorMessage(errorMessageText);
    }
  };

  const checkOrderStatus = async () => {
    const res = await client.query({
      query: GET_CHECKOUT_DATA,
      fetchPolicy: 'network-only',
    });

    if (res?.data?.getCheckoutData === null && !instoreOrderProcessed) {
      setInstoreOrderProcessed(true);
    }
    if (res?.data?.getCheckoutData === null && instoreOrderProcessed) {
      setTimeout(() => {
        history.push('/');
      }, 3500);
    }
  };

  const onFinancingButtonClick = () => {
    const storeDetails = getStoreDetails();
    setError(false);
    setErrorMessage('');

    if (storeDetails) {
      return invokeInstoreAffirm();
    }
    return invokeAffirm();
  };

  const uiLoading = placeOrderLoading || loading;

  return (
    <div className="financing-option mt-0 text-left">
      <div className="my-4">
        <div className="basis-full min-w-0">
          <div className="items-start flex flex-wrap flex-row finance-heading mt-[1.5625rem] text-sm text-gray font-normal text-left">
            <div>
              <div className="text-lg font-bold">Pay Over Time</div>

              <p className="text-base">
                Joybird has teamed up with our financing partners to offer
                financing on your order, starting at 0% APR.
              </p>
            </div>
          </div>

          <div className="items-start flex flex-wrap flex-row finance-heading mt-[1.5625rem] text-sm text-gray font-normal text-left">
            <div>
              <div className="text-lg font-bold">Flexible Monthly Payments</div>

              <p className="text-base">
                Get a real-time decision and choose how long you&apos;d like to
                make fixed monthly payments.
              </p>
            </div>
          </div>

          <div className="items-start flex flex-wrap flex-row finance-heading mt-[1.5625rem] text-sm text-gray font-normal text-left">
            <div>
              <div className="text-lg font-bold">
                See if you&apos;re eligible - it won&apos;t affect your credit
                score
              </div>

              <p className="text-base">
                Just provide a few pieces of information and we&apos;ll check if
                you are qualified for any offers instant from our partners.
              </p>
            </div>
          </div>
        </div>
      </div>
      {checkout && <ShippingInfoBlock checkout={checkout} />}
      {error && (
        <div className="text-sm font-normal text-red mt-8 text-right">
          {errorMessage}
        </div>
      )}
      <div className="flex flex-wrap flex-row items-stretch justify-end my-[30px]">
        {affirmInStore && (
          <CheckoutSubmitButton
            dataXc="place-order-affirm-btn"
            type="submit"
            disabled={uiLoading}
            onClick={checkOrderStatus}
            className="cta btb-grey"
            isLoading={uiLoading}
          >
            {instoreOrderProcessed
              ? 'Order Processed'
              : 'Please complete your order in Affirm'}
          </CheckoutSubmitButton>
        )}
        {!affirmInStore && (
          <CheckoutSubmitButton
            data-xc="place-order-affirm-btn"
            className="cta btb-grey"
            disabled={uiLoading}
            onClick={onFinancingButtonClick}
            isLoading={uiLoading}
          >
            Continue to Financing
          </CheckoutSubmitButton>
        )}
      </div>
    </div>
  );
};

FinancingOption.propTypes = {
  calculateTotal: PropTypes.func,
  checkout: PropTypes.shape({
    selectedStoreEmployee: PropTypes.string,
    deliveryInfo: PropTypes.object,
    email_address: PropTypes.string,
    selected_tab: PropTypes.string,
    shipping: PropTypes.shape({
      address: PropTypes.string,
      city: PropTypes.string,
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      phone_number: PropTypes.string,
      state: PropTypes.string,
      zipcode: PropTypes.string,
    }),
    tab_progress: PropTypes.number,
    total: PropTypes.shape({
      amount: PropTypes.number,
      currency: PropTypes.string,
    }),
  }),
  client: PropTypes.shape({
    mutate: PropTypes.func,
    query: PropTypes.func,
  }),
  getCartIssues: PropTypes.func,
  history: PropTypes.shape({
    push: PropTypes.func,
  }),
  placeOrderLoading: PropTypes.bool,
  showError: PropTypes.func,
};

export default FinancingOption;
